// Phase 129 Plan 09 (Codex review hardening) — handoff JWT plumbing.
//
// The marketing site (web/) generates `https://app.hookmyapp.com/signup?handoff=<jwt>`
// via `web/app/lib/handoff.ts::buildSignupUrl`. The app frontend's job is:
//
//   1. On `/signup` mount: extract `?handoff=<jwt>` and persist it to
//      sessionStorage (this module's `setHandoffJwt`).
//   2. After WorkOS signs the user in and the SPA boots, the FIRST authed
//      backend request must carry `X-Handoff-JWT: <jwt>`. The header is
//      consumed by `backend/src/auth/auth.guard.ts::extractHandoffJwt` and
//      forwarded into `AuthService.applyHandoffIfPresent`, which adopts the
//      anonymous demo SandboxSession into the new workspace (origin flips
//      `demo_anonymous` → `demo_handoff`).
//   3. The header is ONE-SHOT: cleared from sessionStorage immediately after
//      the first read so subsequent authed requests do not re-send it. The
//      backend tolerates expired/invalid tokens silently, but re-sending a
//      stale token on every call would be both wasteful and surprising in
//      logs.
//
// sessionStorage (not localStorage) is the right scope: the JWT is bound to
// the current browser tab + lifecycle, expires in 10 minutes server-side
// (HandoffJwtService), and should not leak across tabs or survive a browser
// restart.
//
// SSR / non-browser safety: every accessor guards on `typeof window` so the
// module is import-safe in vitest's default `node` environment. Tests that
// exercise the full flow opt into the `jsdom` environment via the
// `// @vitest-environment jsdom` pragma.

const HANDOFF_JWT_STORAGE_KEY = 'hookmyapp:demo:handoff_jwt';
// Set alongside the JWT, but cleared independently: the JWT is consumed by
// whichever authed fetch fires first (header path), while this flag survives
// until the post-login landing decision so RootRedirect can send the user to
// Quick Start exactly once after a demo handoff.
const HANDOFF_REDIRECT_STORAGE_KEY = 'hookmyapp:demo:handoff_redirect_pending';

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/**
 * Persist a handoff JWT to sessionStorage. No-op when the value is empty or
 * sessionStorage is unavailable.
 */
export function setHandoffJwt(jwt: string): void {
  if (!hasSessionStorage()) return;
  if (!jwt) return;
  try {
    window.sessionStorage.setItem(HANDOFF_JWT_STORAGE_KEY, jwt);
    window.sessionStorage.setItem(HANDOFF_REDIRECT_STORAGE_KEY, '1');
  } catch {
    // Quota / privacy-mode failures: the handoff is a nice-to-have, never
    // block signup. The user proceeds without demo continuity.
  }
}

/**
 * True when a demo-handoff JWT was captured in this tab and the user has not
 * yet been landed on Quick Start. Read-only — RootRedirect peeks this to pick
 * the post-login landing; QuickStartPage clears it on mount (StrictMode-safe:
 * peeking has no side effect, so double renders/mounts agree on the target).
 */
export function peekHandoffRedirectPending(): boolean {
  if (!hasSessionStorage()) return false;
  try {
    return window.sessionStorage.getItem(HANDOFF_REDIRECT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** One-shot done-marker — called when Quick Start actually mounts. */
export function clearHandoffRedirectPending(): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(HANDOFF_REDIRECT_STORAGE_KEY);
  } catch {
    // Ignore — worst case the next visit to `/` in this tab redirects again.
  }
}

/**
 * One-shot read: returns the stored handoff JWT (or null) and removes it
 * from sessionStorage in the same call. The next read returns null.
 *
 * Designed for the authed fetch paths (`hooks/use-fetch.ts` +
 * `contexts/workspace-context.tsx`'s internal `authFetch`) — whichever
 * fires first wins the JWT and the other gets null. Either path successfully
 * delivers the header to the backend; the FIRST request is what matters
 * because that's the one that triggers `applyHandoffIfPresent` inside
 * `runSignupWithUser`.
 */
export function consumeHandoffJwt(): string | null {
  if (!hasSessionStorage()) return null;
  try {
    const value = window.sessionStorage.getItem(HANDOFF_JWT_STORAGE_KEY);
    if (value) {
      window.sessionStorage.removeItem(HANDOFF_JWT_STORAGE_KEY);
    }
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

/**
 * Test-only / diagnostic accessor — peeks at the stored value without
 * consuming it. Production code MUST use `consumeHandoffJwt`.
 */
export function peekHandoffJwt(): string | null {
  if (!hasSessionStorage()) return null;
  try {
    const value = window.sessionStorage.getItem(HANDOFF_JWT_STORAGE_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

/** Exposed so tests can clear state between cases without hard-coding the string. */
export const HANDOFF_JWT_STORAGE_KEY_FOR_TESTS = HANDOFF_JWT_STORAGE_KEY;
